import { fromJS, List, Map } from 'immutable';

import { IRequestStatus } from '../../declarations';
import { ResaleClosingDashboardActions } from './actionTypeMap';
import * as actionTypes from './actionTypes';

export const initialState = fromJS({
  claimListingOfferByTokenRequestStatus: { isLoading: false },
  generateSellerResponseRequestStatus: { isLoading: false },
  listingOffer: undefined,
  listingOfferError: undefined,
  listingOfferIsLoading: true,
  listingOffers: undefined,
  listingOffersError: undefined,
  listingOffersIsLoading: true,
  removeChecklistDocumentRequests: Map(),
  updateBuyerRepairPreferencesRequestStatus: { isLoading: false },
  uploadChecklistDocumentRequests: Map(),
});

export default function reducer(state = initialState, action: ResaleClosingDashboardActions) {
  switch (action.type) {
    case actionTypes.BEGIN_LOADING_LISTING_OFFER:
      return state.merge({
        listingOfferError: initialState.listingOfferError,
        listingOfferIsLoading: true,
      });
    case actionTypes.RECEIVE_LISTING_OFFER:
      return state.merge({
        listingOffer: fromJS(action.payload.listingOffer),
        listingOfferIsLoading: false,
      });
    case actionTypes.ERROR_LISTING_OFFER:
      return state.merge({
        listingOfferError: action.payload.error,
        listingOfferIsLoading: false,
      });
    case actionTypes.CLEAR_LISTING_OFFER:
      return state.merge({ listingOffer: initialState.listingOffer });
    case actionTypes.BEGIN_LOADING_LISTING_OFFERS:
      return state.merge({
        listingOffersError: initialState.listingOffersError,
        listingOffersIsLoading: true,
      });
    case actionTypes.RECEIVE_LISTING_OFFERS:
      return state.merge({
        listingOffers: fromJS(action.payload.listingOffers),
        listingOffersIsLoading: false,
      });
    case actionTypes.ERROR_LISTING_OFFERS:
      return state.merge({
        listingOffersError: action.payload.error,
        listingOffersIsLoading: false,
      });
    case actionTypes.CLEAR_LISTING_OFFERS:
      return state.merge({ listingOffers: initialState.listingOffers });
    case actionTypes.BEGIN_UPLOAD_CHECKLIST_DOCUMENT:
      return state.merge({
        uploadChecklistDocumentRequests: state
          .get('uploadChecklistDocumentRequests')
          .set(action.payload.requestId, { error: undefined, isLoading: true } as IRequestStatus),
      });
    case actionTypes.SUCCESS_UPLOAD_CHECKLIST_DOCUMENT:
      return state.merge({
        uploadChecklistDocumentRequests: state
          .get('uploadChecklistDocumentRequests')
          .set(action.payload.requestId, { error: undefined, isLoading: false } as IRequestStatus),
      });
    case actionTypes.ERROR_UPLOAD_CHECKLIST_DOCUMENT:
      return state.merge({
        uploadChecklistDocumentRequests: state
          .get('uploadChecklistDocumentRequests')
          .set(action.payload.requestId, {
            error: action.payload.error,
            isLoading: false,
          } as IRequestStatus),
      });
    case actionTypes.BEGIN_REMOVE_CHECKLIST_DOCUMENT:
      return state.merge({
        removeChecklistDocumentRequests: state
          .get('removeChecklistDocumentRequests')
          .set(action.payload.documentId, { error: undefined, isLoading: true } as IRequestStatus),
      });
    case actionTypes.SUCCESS_REMOVE_CHECKLIST_DOCUMENT:
      return state.merge({
        removeChecklistDocumentRequests: state
          .get('removeChecklistDocumentRequests')
          .set(action.payload.documentId, { error: undefined, isLoading: false } as IRequestStatus),
      });
    case actionTypes.ERROR_REMOVE_CHECKLIST_DOCUMENT:
      return state.merge({
        removeChecklistDocumentRequests: state
          .get('removeChecklistDocumentRequests')
          .set(action.payload.documentId, {
            error: action.payload.error,
            isLoading: false,
          } as IRequestStatus),
      });
    case actionTypes.UPDATE_CHECKLIST_ITEM:
      const { checklistItem } = action.payload;
      const itemKeys = ['listingOffer', 'document_checklist', 'document_checklist_items'];

      const checklistItems = state.getIn(itemKeys, List());
      const itemIndex = checklistItems.findIndex(item => item.get('id') === checklistItem.id);

      return state.setIn(
        itemKeys,
        itemIndex === -1
          ? checklistItems.push(fromJS(checklistItem))
          : checklistItems.set(itemIndex, fromJS(checklistItem)),
      );
    case actionTypes.REQUEST_GENERATE_SELLER_RESPONSE:
      return state.merge({
        generateSellerResponseRequestStatus: {
          error: undefined,
          isLoading: true,
        } as IRequestStatus,
      });
    case actionTypes.SUCCESS_REQUEST_GENERATE_SELLER_RESPONSE:
      return state.merge({
        generateSellerResponseRequestStatus: {
          error: undefined,
          isLoading: false,
        } as IRequestStatus,
      });
    case actionTypes.ERROR_REQUEST_GENERATE_SELLER_RESPONSE:
      const { error: generateResponseError } = action.payload;
      return state.merge({
        generateSellerResponseRequestStatus: {
          error: generateResponseError,
          isLoading: false,
        } as IRequestStatus,
      });
    case actionTypes.RECEIVE_CUSTOMER_REQUEST:
      const { customerRequest } = action.payload;
      const requestKeys = ['listingOffer', 'customer_requests'];

      const customerRequests = state.getIn(requestKeys, List());
      const requestIndex = customerRequests.findIndex(
        item => item.get('id') === customerRequest.id,
      );

      return state.setIn(
        requestKeys,
        requestIndex === -1
          ? customerRequests.push(fromJS(customerRequest))
          : customerRequests.set(requestIndex, fromJS(customerRequest)),
      );
    case actionTypes.BEGIN_UPDATE_BUYER_REPAIR_PREFERENCES:
      return state.merge({
        updateBuyerRepairPreferencesRequestStatus: {
          error: undefined,
          isLoading: true,
        } as IRequestStatus,
      });
    case actionTypes.SUCCESS_REMOVE_CHECKLIST_DOCUMENT:
      return state.merge({
        updateBuyerRepairPreferencesRequestStatus: {
          error: undefined,
          isLoading: false,
        } as IRequestStatus,
      });
    case actionTypes.ERROR_REMOVE_CHECKLIST_DOCUMENT:
      const { error } = action.payload;
      return state.merge({
        updateBuyerRepairPreferencesRequestStatus: { isLoading: false, error } as IRequestStatus,
      });
    case actionTypes.BEGIN_CLAIM_LISTING_OFFER_BY_TOKEN:
      return state.merge({
        claimListingOfferByTokenRequestStatus: {
          error: undefined,
          isLoading: true,
        } as IRequestStatus,
      });
    case actionTypes.SUCCESS_CLAIM_LISTING_OFFER_BY_TOKEN:
      return state.merge({
        claimListingOfferByTokenRequestStatus: {
          error: undefined,
          isLoading: false,
        } as IRequestStatus,
      });
    case actionTypes.ERROR_CLAIM_LISTING_OFFER_BY_TOKEN:
      return state.merge({
        claimListingOfferByTokenRequestStatus: {
          error: action.payload.error,
          isLoading: false,
        } as IRequestStatus,
      });
    default:
      return state;
  }
}
